import {ChangeEvent, SyntheticEvent, useEffect, useMemo, useState} from "react";
import {DropdownProps} from "semantic-ui-react";
import agent from "../../agent";
import {TaskModel, TaskStatus} from "../workTableTask/TaskModel";
import {DoctorOptions} from "../dashboard";
import {
    ImportDataProvider,
    ImportExecuteRequest,
    ImportExecuteResponse,
    ImportEntityType,
    ImportAliasMap,
    ImportMappingConfig,
    ImportMapping,
    ImportSessionResponse
} from "../../services/impl/ImportDataProvider";
import {
    buildInitialMapping,
    buildMappingFromConfig,
    EMPTY_COLUMN_OPTION,
    MappingTarget,
    getMappingError,
    getRequiredTargets,
    mapDoctorOptions,
} from "./ImportDataPageHelpers";
import {ImportUploadResponseWithMapping} from "./ImportDataPageHelpers";
import {DOCTOR_TITLE_A_LOWER, PATIENT_TITLE_OV} from "./ImportDataPageHelpers";

const importDataProvider = new ImportDataProvider();

const upsertById = <T extends {_id: string}>(items: T[], item: T): T[] => {
    const next = items.filter((entry) => entry._id !== item._id);
    return [item, ...next];
};

export const useImportDataPage = () => {
    const [entityType, setEntityType] = useState<ImportEntityType>("patient");
    const [file, setFile] = useState<File | undefined>(undefined);
    const [session, setSession] = useState<ImportSessionResponse | undefined>(undefined);
    const [mapping, setMapping] = useState<ImportMapping>({});
    const [createdTask, setCreatedTask] = useState<TaskModel | undefined>(undefined);
    const [uploading, setUploading] = useState<boolean>(false);
    const [executing, setExecuting] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [infoMessage, setInfoMessage] = useState<string>("");
    const [taskId, setTaskId] = useState<string>("");
    const [task, setTask] = useState<TaskModel | undefined>(undefined);
    const [taskLoading, setTaskLoading] = useState<boolean>(false);
    const [skipErrors, setSkipErrors] = useState<boolean>(true);
    const [detailedLog, setDetailedLog] = useState<boolean>(true);
    const [restoreDeleted, setRestoreDeleted] = useState<boolean>(false);
    const [defaultDoctorId, setDefaultDoctorId] = useState<string>("");
    const [doctorOptions, setDoctorOptions] = useState<DoctorOptions[]>([]);
    const [loadingDoctors, setLoadingDoctors] = useState<boolean>(false);
    const [targetFields, setTargetFields] = useState<MappingTarget[]>([]);
    const [loadingTargetFields, setLoadingTargetFields] = useState<boolean>(false);
    const [fieldAliases, setFieldAliases] = useState<ImportAliasMap>({});
    const [mappingConfigs, setMappingConfigs] = useState<ImportMappingConfig[]>([]);
    const [selectedConfigId, setSelectedConfigId] = useState<string>("");
    const [loadingConfigs, setLoadingConfigs] = useState<boolean>(false);
    const [savingConfig, setSavingConfig] = useState<boolean>(false);
    const [deletingConfig, setDeletingConfig] = useState<boolean>(false);

    const requiredTargets = useMemo(() => getRequiredTargets(targetFields), [targetFields]);
    const mappingOptions = useMemo(() => [EMPTY_COLUMN_OPTION, ...targetFields], [targetFields]);
    const mappingError = useMemo(() => getMappingError(mapping, requiredTargets, targetFields), [mapping, requiredTargets, targetFields]);
    const isUidMapped = useMemo(() => Object.values(mapping).includes("UID"), [mapping]);
    const isImportEnabled = window.config?.importEnabled === true;
    const needsDefaultDoctor = entityType === "patient";
    const isDefaultDoctorMissing = needsDefaultDoctor && !defaultDoctorId;
    const doctorSelectionError = isDefaultDoctorMissing ? `Необходимо выбрать ${DOCTOR_TITLE_A_LOWER} по умолчанию для импорта новых ${PATIENT_TITLE_OV}.` : "";
    const canStartImport = useMemo(() => !mappingError && !isDefaultDoctorMissing && !loadingTargetFields && !!session, [mappingError, isDefaultDoctorMissing, loadingTargetFields, session]);
    const canSaveCurrentConfig = Boolean(session && !savingConfig && Object.keys(mapping).length > 0);
    const configOptions = useMemo(() => mappingConfigs.map((config) => ({
        key: config._id,
        value: config._id,
        text: config.name
    })), [mappingConfigs]);
    const usedTargets = useMemo(() => {
        const used: Record<string, string> = {};
        Object.entries(mapping).forEach(([source, target]) => {
            if (target) {
                used[target] = source;
            }
        });
        return used;
    }, [mapping]);

    useEffect(() => {
        let isCancelled = false;

        const loadTargetFields = async () => {
            setLoadingTargetFields(true);
            setError("");

            const fieldsResponse = await importDataProvider.getMeta(entityType);

            if (isCancelled) {
                return;
            }

            if (!fieldsResponse?.fields || fieldsResponse.fields.length === 0) {
                setError("Не удалось загрузить список полей для сопоставления");
                setTargetFields([]);
                setFieldAliases({});
                return;
            }

            setTargetFields(fieldsResponse.fields);
            setFieldAliases(fieldsResponse.aliases || {});
        };

        void loadTargetFields().finally(() => {
            if (!isCancelled) {
                setLoadingTargetFields(false);
            }
        });

        return () => {
            isCancelled = true;
            setLoadingTargetFields(false);
            setTargetFields([]);
            setFieldAliases({});
        };
    }, [entityType]);

    useEffect(() => {
        let isCancelled = false;

        const loadConfigs = async () => {
            setLoadingConfigs(true);
            const loaded = await importDataProvider.getConfigs(entityType);
            if (isCancelled) {
                return;
            }

            if (!loaded || !Array.isArray(loaded)) {
                setMappingConfigs([]);
                setSelectedConfigId("");
                return;
            }

            const normalized = loaded
                .filter((config) => !!config && typeof config._id === "string")
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

            setMappingConfigs(normalized);
        };

        void loadConfigs().finally(() => {
            if (!isCancelled) {
                setLoadingConfigs(false);
            }
        });

        return () => {
            isCancelled = true;
            setLoadingConfigs(false);
            setMappingConfigs([]);
            setSelectedConfigId("");
        };
    }, [entityType]);

    const handleEntityChange = (_event: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        const selected = data.value as ImportEntityType;
        setEntityType(selected);
        setSession(undefined);
        setTargetFields([]);
        setFieldAliases({});
        setTaskId("");
        setTask(undefined);
        setTaskLoading(false);
        setCreatedTask(undefined);
        setError("");
        setInfoMessage("");
        setMapping({});
        setSelectedConfigId("");
        setDefaultDoctorId("");
        if (file) {
            setFile(undefined);
            const fileInput = document.getElementById("import-data-file") as HTMLInputElement | null;
            if (fileInput) {
                fileInput.value = "";
            }
        }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setError("");
        setInfoMessage("");
        setTaskId("");
        setTask(undefined);
        setCreatedTask(undefined);
        setTaskLoading(false);
        const selectedFile = event.target.files?.[0];
        setFile(selectedFile);
    };

    const normalizeCurrentMapping = (current: ImportMapping = mapping): ImportMapping => Object.fromEntries(
        Object.entries(current).filter(([, targetField]) => !!targetField)
    ) as ImportMapping;

    const readFileAndPreview = async () => {
        if (!file) {
            setError("Выберите файл");
            return;
        }
        setUploading(true);
        setError("");
        setInfoMessage("");
        setTaskId("");
        setTask(undefined);
        setCreatedTask(undefined);
        setTaskLoading(false);
        if (loadingTargetFields) {
            setError("Подождите, загружаются поля для сопоставления");
            setUploading(false);
            return;
        }
        if (targetFields.length === 0) {
            setError("Не найден список полей для импорта. Проверьте подключение к серверу.");
            setUploading(false);
            return;
        }

        const loaded = await importDataProvider.upload(file, entityType);
        if (!loaded) {
            setUploading(false);
            setError("Не удалось загрузить файл");
            return;
        }

        const importResponse = loaded as ImportUploadResponseWithMapping;
        const sourceForInitialMapping = importResponse.mapping;
        const initialMapping = buildInitialMapping(importResponse, entityType, targetFields, sourceForInitialMapping, fieldAliases);

        setSession({
            ...importResponse,
            fileSize: file.size,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            fileName: importResponse.fileName,
        });
        setMapping(initialMapping);
        setSelectedConfigId("");
        setUploading(false);
        setInfoMessage("Файл успешно загружен. Проверьте автоопределение и настройте сопоставление колонок.");
    };

    const resetSession = () => {
        setSession(undefined);
        setMapping({});
        setSelectedConfigId("");
        setDefaultDoctorId("");
        setTaskId("");
        setTask(undefined);
        setCreatedTask(undefined);
        setError("");
        setInfoMessage("");
        setTaskLoading(false);
        setFile(undefined);
        const fileInput = document.getElementById("import-data-file") as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = "";
        }
    };

    const resetUploadedFileState = () => {
        setSession(undefined);
        setMapping({});
        setSelectedConfigId("");
        setDefaultDoctorId("");
        setFile(undefined);
        const fileInput = document.getElementById("import-data-file") as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = "";
        }
    };

    useEffect(() => {
        if (entityType !== "patient") {
            setDoctorOptions([]);
            setLoadingDoctors(false);
            return;
        }

        let isCancelled = false;

        const loadDoctors = async () => {
            setLoadingDoctors(true);
            try {
                const response = await agent.DoctorProvider.getList({page: 1, limit: 9999, search: ""});
                if (isCancelled) {
                    return;
                }

                setDoctorOptions(mapDoctorOptions((response && Array.isArray(response.docs) ? response.docs : [])));
            } catch (error) {
                if (!isCancelled) {
                    setDoctorOptions([]);
                    console.error("Не удалось загрузить список социальных кураторов", error);
                }
            } finally {
                if (!isCancelled) {
                    setLoadingDoctors(false);
                }
            }
        };

        loadDoctors();

        return () => {
            isCancelled = true;
        };
    }, [entityType]);

    const onMappingChange = (sourceColumn: string, targetField: string) => {
        setMapping((current) => {
            const next = {...current};
            if (!targetField) {
                delete next[sourceColumn];
            } else {
                next[sourceColumn] = targetField;
            }
            return next;
        });
    };

    const onConfigSelect = (_event: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        const configId = (data.value || "") as string;
        setSelectedConfigId(configId);

        if (!configId) {
            return;
        }

        const selected = mappingConfigs.find((config) => config._id === configId);
        if (!session || !selected) {
            return;
        }

        const configMapping = buildMappingFromConfig(session, entityType, targetFields, selected.mapping, fieldAliases);
        setMapping(configMapping);
        if (Object.keys(configMapping).length === 0) {
            setInfoMessage(`Конфигурация "${selected.name}" выбрана, но подходящих колонок в текущем файле не найдено.`);
            return;
        }
        setInfoMessage(`Загружена конфигурация "${selected.name}"`);
    };

    const saveCurrentConfig = async () => {
        if (!session) {
            setError("Нет загруженной сессии импорта");
            return;
        }

        const enteredName = window.prompt("Введите имя новой конфигурации");
        if (enteredName === null) {
            return;
        }

        const trimmedName = enteredName.trim();
        if (!trimmedName) {
            setError("Введите имя конфигурации");
            return;
        }

        setSavingConfig(true);
        setError("");
        setInfoMessage("");

        const normalizedMapping = normalizeCurrentMapping();
        const result = await importDataProvider.saveConfig({
            name: trimmedName,
            entityType,
            mapping: normalizedMapping
        });

        if (!result || !result._id) {
            setError("Не удалось сохранить конфигурацию");
            setSavingConfig(false);
            return;
        }

        setMappingConfigs((current) => upsertById(current, result));
        setSelectedConfigId(result._id);
        setSavingConfig(false);
        setInfoMessage("Конфигурация сохранена.");
    };

    const deleteSelectedConfig = async () => {
        if (!selectedConfigId) {
            setError("Выберите конфигурацию для удаления");
            return;
        }

        const selected = mappingConfigs.find((config) => config._id === selectedConfigId);
        const configName = selected?.name || "выбранную конфигурацию";
        if (!window.confirm(`Удалить конфигурацию "${configName}"?`)) {
            return;
        }

        setDeletingConfig(true);
        setError("");
        setInfoMessage("");

        const result = await importDataProvider.deleteConfig(selectedConfigId);
        setDeletingConfig(false);

        if (!result?.success) {
            setError("Не удалось удалить конфигурацию");
            return;
        }

        setMappingConfigs((current) => current.filter((config) => config._id !== selectedConfigId));
        setSelectedConfigId("");
        setInfoMessage("Конфигурация удалена.");
    };

    const refreshTaskStatus = async () => {
        if (!taskId) {
            return;
        }
        setTaskLoading(true);
        try {
            const loadedTask = await agent.TaskProvider.open(taskId);
            if (loadedTask && typeof loadedTask === "object" && "status" in loadedTask) {
                setTask(loadedTask);
            }
        } finally {
            setTaskLoading(false);
        }
    };

    useEffect(() => {
        if (!taskId) {
            return;
        }

        let isCancelled = false;
        let timer: ReturnType<typeof setInterval> | undefined;

        const poll = async () => {
            if (isCancelled || !taskId) {
                return;
            }
            setTaskLoading(true);
            try {
                const loadedTask = await agent.TaskProvider.open(taskId);
                if (isCancelled || !loadedTask || typeof loadedTask !== "object" || !("status" in loadedTask)) {
                    return;
                }
                setTask(loadedTask);
                if (loadedTask.status === TaskStatus.completed || loadedTask.status === TaskStatus.cancelled) {
                    if (timer) {
                        clearInterval(timer);
                    }
                }
            } finally {
                if (!isCancelled) {
                    setTaskLoading(false);
                }
            }
        };

        poll().then(() => undefined);
        timer = setInterval(poll, 5000);

        return () => {
            isCancelled = true;
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [taskId]);

    const startImport = async () => {
        if (!session) {
            setError("Нет загруженной сессии импорта");
            return;
        }
        const nextError = getMappingError(mapping, requiredTargets, targetFields);
        if (nextError) {
            setError(nextError);
            return;
        }
        if (isDefaultDoctorMissing) {
            setError(doctorSelectionError);
            return;
        }

        setExecuting(true);
        setError("");
        setInfoMessage("");
        setTask(undefined);
        setTaskLoading(true);
        setCreatedTask(undefined);

        const normalizedMapping = normalizeCurrentMapping();

        const requestData: ImportExecuteRequest = {
            importSessionId: session.importSessionId,
            entityType,
            mapping: normalizedMapping,
            options: {
                skipErrors,
                detailedLog,
                restoreDeleted,
                ...(entityType === "patient" ? {defaultDoctorId} : {})
            }
        };

        const result = await importDataProvider.execute(requestData) as ImportExecuteResponse | undefined;
        setExecuting(false);
        setTaskLoading(false);

        if (!result?.taskId) {
            setError("Не удалось запустить задачу импорта");
            return;
        }

        const createdTaskModel = await agent.TaskProvider.open(result.taskId);
        if (!createdTaskModel || typeof createdTaskModel !== "object") {
            setError(result.message || "Не удалось открыть созданную задачу");
            return;
        }

        setTaskId(result.taskId);
        setTask(createdTaskModel);
        setCreatedTask(createdTaskModel);
        resetUploadedFileState();
        setInfoMessage(result.message);
    };

    return {
        entityType,
        file,
        session,
        mapping,
        createdTask,
        uploading,
        executing,
        error,
        infoMessage,
        taskId,
        task,
        taskLoading,
        skipErrors,
        detailedLog,
        restoreDeleted,
        defaultDoctorId,
        doctorOptions,
        loadingDoctors,
        targetFields,
        mappingOptions,
        mappingError,
        configOptions,
        selectedConfigId,
        savingConfig,
        deletingConfig,
        loadingConfigs,
        canSaveCurrentConfig,
        isUidMapped,
        isImportEnabled,
        isDefaultDoctorMissing,
        doctorSelectionError,
        canStartImport,
        usedTargets,
        handleEntityChange,
        handleFileChange,
        readFileAndPreview,
        resetSession,
        onMappingChange,
        onConfigSelect,
        saveCurrentConfig,
        deleteSelectedConfig,
        refreshTaskStatus,
        startImport,
        setDefaultDoctorId,
        setSkipErrors,
        setDetailedLog,
        setRestoreDeleted,
        closeCreatedTask: () => setCreatedTask(undefined),
    };
};

export default useImportDataPage;
